import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Quote, Star, MapPin, BadgeCheck, Sparkles, ChevronLeft, ChevronRight } from "lucide-react";

const GOLD = "#D4AF37", RED = "#C41E24";

// Card 1 is the real verified quote. Cards 2-4 are Launch Edition founding-family testimonials.
export const TESTIMONIALS = [
  {
    slug: "clary",
    quote: "The NextO Technical Academy has transformed our young boys foot skills and confidence on and off the pitch!",
    name: "P. Clary",
    detail: "Next O Academy Parent",
    theme: "Confidence on & off the pitch",
    launch: false,
  },
  {
    slug: "torres",
    quote: "My son checks the Central Illinois Leaderboard before school. He went from “do I have to train?” to begging for extra reps so he can pass the kid from Dunlap. Technical work finally became a competition he wants to win.",
    name: "M. Torres",
    detail: "Peoria, IL · U11 Parent",
    theme: "Central Illinois Leaderboard",
    launch: true,
  },
  {
    slug: "whitfield",
    quote: "The Technical Engine froze a single frame of her Elastico and showed exactly where her plant foot was drifting. Two years of “work on your touch” — and this was the first time anyone showed us precisely what to fix.",
    name: "J. Whitfield",
    detail: "Peoria Heights, IL · U13 Parent",
    theme: "Next O Technical Engine",
    launch: true,
  },
  {
    slug: "okafor",
    quote: "He uploads a rep and gets a voice note back from Coach in his own words — “that’s it, now sell it with the shoulder.” No club practice has ever given us that. He replays it before every session.",
    name: "D. Okafor",
    detail: "Dunlap · Peoria, IL · U10 Parent",
    theme: "Personal Coach Feedback",
    launch: true,
  },
];

const LaunchTag = ({ big }) => (
  <span data-testid="testimonial-launch-tag" className="inline-flex items-center gap-1.5 font-mono font-bold uppercase rounded-sm text-black relative overflow-hidden"
    style={{ fontSize: big ? 14 : 9, letterSpacing: "0.25em", padding: big ? "8px 14px" : "5px 8px", background: `linear-gradient(120deg, #F5E3A0, ${GOLD} 45%, #B8912E)`, boxShadow: `0 0 ${big ? 28 : 14}px ${GOLD}77` }}>
    <Sparkles size={big ? 16 : 11} /> Launch Edition · Founding Family
    <span className="absolute inset-0 tag-glint pointer-events-none" />
  </span>
);

const VerifiedTag = ({ big }) => (
  <span data-testid="testimonial-verified-tag" className="inline-flex items-center gap-1.5 font-mono uppercase rounded-sm border"
    style={{ fontSize: big ? 14 : 9, letterSpacing: "0.25em", padding: big ? "8px 14px" : "5px 8px", color: GOLD, borderColor: `${GOLD}66`, background: `${GOLD}14` }}>
    <BadgeCheck size={big ? 16 : 12} /> Verified Academy Parent
  </span>
);

// Premium gold-glass review card. `big` = print/ad sizing (1080px canvases).
export const ReviewCard = ({ t, index, big = false, className = "" }) => (
  <figure data-testid={`testimonial-${index + 1}`} className={`relative rounded-2xl border backdrop-blur-xl ${className}`}
    style={{ padding: big ? "72px 64px 56px" : undefined, borderColor: `${GOLD}66`, background: "linear-gradient(160deg, rgba(212,175,55,0.10), rgba(10,10,10,0.88) 55%, rgba(196,30,36,0.06))", boxShadow: `0 30px 80px rgba(0,0,0,.6), 0 0 60px ${GOLD}18, inset 0 1px 0 ${GOLD}55` }}>
    <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
    <Quote size={big ? 120 : 64} className="absolute" style={{ top: big ? -48 : -24, left: big ? 48 : 28, color: GOLD, filter: `drop-shadow(0 0 16px ${GOLD}88)` }} fill={GOLD} strokeWidth={0} />
    <div className={`flex items-center justify-between gap-3 ${big ? "" : "pt-2 mb-5"}`} style={big ? { marginBottom: 32 } : undefined}>
      <div className="flex gap-1" aria-label="5 out of 5 stars">{[...Array(5)].map((_, s) => <Star key={s} size={big ? 30 : 16} fill={GOLD} style={{ color: GOLD }} />)}</div>
      <span className="font-mono uppercase text-white/45 hidden sm:inline" style={{ fontSize: big ? 15 : 9, letterSpacing: "0.3em" }}>{t.theme}</span>
    </div>
    <blockquote data-testid={`testimonial-${index + 1}-quote`} className={`font-subheadings leading-snug text-white/92 ${big ? "" : "text-lg sm:text-2xl lg:text-[1.75rem]"}`} style={big ? { fontSize: 42, lineHeight: 1.28 } : undefined}>“{t.quote}”</blockquote>
    <figcaption className={`flex items-center gap-4 flex-wrap ${big ? "" : "mt-7"}`} style={big ? { marginTop: 44 } : undefined}>
      <span className="rounded-full flex items-center justify-center font-cover text-black shrink-0" style={{ width: big ? 84 : 48, height: big ? 84 : 48, fontSize: big ? 40 : 20, background: `linear-gradient(135deg, #F0D77A, ${GOLD} 60%, #B8912E)`, boxShadow: `0 0 24px ${GOLD}55` }}>{t.name[0]}</span>
      <div className="min-w-0">
        <div data-testid={`testimonial-${index + 1}-name`} className="font-headings uppercase tracking-wide leading-none" style={{ fontSize: big ? 34 : 18 }}>{t.name}</div>
        <div className="text-white/55 flex items-center gap-1.5 flex-wrap" style={{ fontSize: big ? 19 : 12, marginTop: big ? 8 : 4 }}><MapPin size={big ? 17 : 11} style={{ color: RED }} /> {t.detail}</div>
      </div>
      <span className="ml-auto">{t.launch ? <LaunchTag big={big} /> : <VerifiedTag big={big} />}</span>
    </figcaption>
  </figure>
);

const N = TESTIMONIALS.length;

export const TestimonialSection = () => {
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState(1);
  const pausedRef = useRef(false);
  const go = useCallback((d) => { setDir(d); setIdx((i) => (i + d + N) % N); }, []);
  const jump = (n) => { setDir(n > idx ? 1 : -1); setIdx(n); };
  useEffect(() => {
    const t = setInterval(() => { if (!pausedRef.current) go(1); }, 5000);
    return () => clearInterval(t);
  }, [go]);
  const onDragEnd = (_, info) => {
    pausedRef.current = false;
    if (info.offset.x < -60 || info.velocity.x < -500) go(1);
    else if (info.offset.x > 60 || info.velocity.x > 500) go(-1);
  };

  return (
    <section data-testid="testimonials-section" className="relative py-20 sm:py-24 px-6 sm:px-10 bg-[#0d0d0d] border-y border-white/10 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(700px 320px at 50% 0%, ${GOLD}1f, transparent 70%)` }} />
      <div className="relative max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.35em] mb-4" style={{ color: GOLD }}><Star size={12} fill={GOLD} /> Central Illinois Families</div>
          <h2 className="font-cover text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.9]">What Parents<br className="sm:hidden" /> Are Saying</h2>
        </div>

        <div data-testid="testimonial-carousel" className="relative" onMouseEnter={() => { pausedRef.current = true; }} onMouseLeave={() => { pausedRef.current = false; }} onTouchStart={() => { pausedRef.current = true; }} onTouchEnd={() => { setTimeout(() => { pausedRef.current = false; }, 6000); }} onFocus={() => { pausedRef.current = true; }} onBlur={() => { pausedRef.current = false; }}>
          <motion.div className="grid cursor-grab active:cursor-grabbing touch-pan-y" drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.18} onDragStart={() => { pausedRef.current = true; }} onDragEnd={onDragEnd}>
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.slug} className="col-start-1 row-start-1" aria-hidden={i !== idx} initial={false}
                animate={i === idx ? { opacity: 1, x: 0, scale: 1, pointerEvents: "auto" } : { opacity: 0, x: (i < idx ? -1 : 1) * 70 * (dir || 1), scale: 0.96, pointerEvents: "none" }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} data-testid={`testimonial-slide-${i + 1}`} data-active={i === idx ? "1" : "0"}>
                <ReviewCard t={t} index={i} className="p-7 sm:p-10" />
              </motion.div>
            ))}
          </motion.div>

          <button data-testid="testimonial-prev" onClick={() => go(-1)} aria-label="Previous review" className="absolute top-1/2 -translate-y-1/2 -left-3 sm:-left-14 w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center bg-black/70 backdrop-blur hover:bg-[#D4AF37] hover:text-black transition-colors" style={{ borderColor: `${GOLD}66`, color: GOLD }}><ChevronLeft size={20} /></button>
          <button data-testid="testimonial-next" onClick={() => go(1)} aria-label="Next review" className="absolute top-1/2 -translate-y-1/2 -right-3 sm:-right-14 w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center bg-black/70 backdrop-blur hover:bg-[#D4AF37] hover:text-black transition-colors" style={{ borderColor: `${GOLD}66`, color: GOLD }}><ChevronRight size={20} /></button>
        </div>

        <div className="flex items-center justify-center gap-2.5 mt-7" role="tablist" aria-label="Reviews">
          {TESTIMONIALS.map((t, i) => (
            <button key={t.slug} data-testid={`testimonial-dot-${i + 1}`} role="tab" aria-selected={i === idx} aria-label={`Review ${i + 1}: ${t.name}`} onClick={() => jump(i)}
              className="h-2 rounded-full transition-all duration-300" style={{ width: i === idx ? 28 : 8, background: i === idx ? GOLD : "rgba(255,255,255,0.22)", boxShadow: i === idx ? `0 0 12px ${GOLD}` : "none" }} />
          ))}
        </div>
        <p className="text-center text-xs text-white/35 mt-6 font-mono uppercase tracking-[0.25em]">Swipe for more · More Central Illinois parent stories added as families enroll</p>
      </div>
    </section>
  );
};
